import {computed, ComputedRef, unref} from 'vue';
import {parseISO} from 'date-fns';
import {I18nService} from '@malt/vue3-i18n';
import {getPageLocale} from '@malt/dom';
import {BriefDetails, BriefStartDateFlexibility, BriefStartDateType, BriefWorkingStatus, FreelancerAvailability} from '@/common/api/api-raw-types';

interface UseBriefConditions {
    readonly isDurationMoreThanOneMonth: (value?: number, unit?: string) => boolean;
    readonly duration: ComputedRef<string | undefined>;
    readonly startDate: ComputedRef<string | undefined>;
    readonly freelancerAvailability: ComputedRef<string>;
    readonly dailyFreelancerRate: ComputedRef<string | undefined>;
    readonly budget: ComputedRef<string | undefined>;
    readonly remote: ComputedRef<string>;
    readonly city: ComputedRef<string>;
}

export function useBriefConditions(props: BriefDetails, pageLocale: string | undefined): UseBriefConditions {
    const briefDetails = unref(props);

    const SHORT_PROJECT_MAX_DAYS = 30;
    const SHORT_PROJECT_MAX_WEEKS = 4;
    const SHORT_PROJECT_MAX_MONTHS = 1;

    const currencyFormatter = Intl.NumberFormat(pageLocale, {
        style: 'currency',
        currency: briefDetails.currencyCode,
        maximumFractionDigits: 0,
    });

    function isDurationMoreThanOneMonth(value?: number, unit?: string): boolean {
        return value && unit
            ? (value >= SHORT_PROJECT_MAX_DAYS && unit === 'DAYS') ||
                  (value >= SHORT_PROJECT_MAX_WEEKS && unit === 'WEEKS') ||
                  (value >= SHORT_PROJECT_MAX_MONTHS && unit === 'MONTHS')
            : (briefDetails.projectDuration &&
                  ((briefDetails.projectDuration.value >= SHORT_PROJECT_MAX_DAYS && briefDetails.projectDuration.unit === 'DAYS') ||
                      (briefDetails.projectDuration.value >= SHORT_PROJECT_MAX_WEEKS && briefDetails.projectDuration.unit === 'WEEKS') ||
                      (briefDetails.projectDuration.value >= SHORT_PROJECT_MAX_MONTHS && briefDetails.projectDuration.unit === 'MONTHS'))) ??
                  false;
    }

    const duration = computed<string | undefined>(() => {
        if (!briefDetails.projectDuration) {
            return undefined;
        }

        return (
            I18nService.icu(`project.proposal.duration.${briefDetails.projectDuration!.unit}`, {
                value: briefDetails.projectDuration!.value,
            }) + (briefDetails.projectDuration!.fixed ? ` - ${I18nService.msg('customer.brief.conditions.project.duration.fixed')}` : '')
        );
    });

    const startDate = computed<string | undefined>(() => {
        if (!briefDetails.startDateType) {
            return undefined;
        }

        const date = new Date(parseISO(briefDetails.startDate!));
        const beginDate = new Date(parseISO(briefDetails.startDate!));
        const endDate = new Date(parseISO(briefDetails.startDate!));
        const options: Intl.DateTimeFormatOptions =
            briefDetails.startDateFlexibility === 'MORE_OR_LESS_1_MONTH'
                ? {month: 'long', year: 'numeric'}
                : {day: 'numeric', month: 'long', year: 'numeric'};
        if (briefDetails.startDateType !== BriefStartDateType.CHOSEN_DATE) {
            return I18nService.msg(`customer.brief.conditions.when.start.date.${briefDetails.startDateType}`);
        }

        switch (briefDetails.startDateFlexibility) {
            case BriefStartDateFlexibility.MORE_OR_LESS_2_DAYS:
                beginDate.setDate(date.getDate() - 2);
                endDate.setDate(date.getDate() + 2);
                return `${new Intl.DateTimeFormat(getPageLocale(), options).format(beginDate)} - 
                     ${new Intl.DateTimeFormat(getPageLocale(), options).format(endDate)}`;
            case BriefStartDateFlexibility.MORE_OR_LESS_1_WEEK:
                beginDate.setDate(date.getDate() - 7);
                endDate.setDate(date.getDate() + 7);
                return `${new Intl.DateTimeFormat(getPageLocale(), options).format(beginDate)} - 
                     ${new Intl.DateTimeFormat(getPageLocale(), options).format(endDate)}`;
            case BriefStartDateFlexibility.MORE_OR_LESS_1_MONTH:
                beginDate.setMonth(date.getMonth() - 1);
                endDate.setMonth(date.getMonth() + 1);
                return `${new Intl.DateTimeFormat(getPageLocale(), options).format(beginDate)} - 
                     ${new Intl.DateTimeFormat(getPageLocale(), options).format(endDate)}`;
            default:
                return new Intl.DateTimeFormat(getPageLocale(), options).format(date);
        }
    });

    const freelancerAvailability = computed<string>(() =>
        I18nService.msg(`customer.brief.conditions.when.frequency.${briefDetails.freelancerAvailability}`),
    );

    const dailyFreelancerRate = computed<string | undefined>(() =>
        briefDetails.dailyFreelancerRate ? currencyFormatter.format(briefDetails.dailyFreelancerRate) : undefined,
    );

    const budget = computed<string | undefined>(() => (briefDetails.budget ? currencyFormatter.format(briefDetails.budget) : undefined));
    const numberFullTimeRemoteDays: number | undefined =
        briefDetails.freelancerAvailability && Object.keys(FreelancerAvailability).indexOf(briefDetails.freelancerAvailability) + 1;

    const remote = computed<string>(() => {
        switch (briefDetails.workingStatus) {
            case BriefWorkingStatus.REMOTELY:
                if (briefDetails.projectDuration && briefDetails.projectDuration.value < 5 && briefDetails.projectDuration.unit === 'DAYS') {
                    return I18nService.msg(`customer.brief.conditions.where.remote.frequency.${briefDetails.projectDuration.value}`);
                }
                return numberFullTimeRemoteDays
                    ? I18nService.msg(`customer.brief.conditions.where.remote.frequency.${numberFullTimeRemoteDays}`)
                    : I18nService.msg(`customer.brief.conditions.where.remote.REMOTE_FULL_TIME`);
            case BriefWorkingStatus.ON_SITE:
                return I18nService.msg('customer.brief.conditions.where.remote.no');
            default:
                return I18nService.msg(`customer.brief.conditions.where.remote.frequency.${briefDetails.remoteFrequency}`);
        }
    });

    const city = computed<string>(() => briefDetails.address?.city || '');

    return {
        isDurationMoreThanOneMonth,
        duration,
        startDate,
        freelancerAvailability,
        dailyFreelancerRate,
        budget,
        remote,
        city,
    };
}
